import { useQuery, useLazyQuery } from '@apollo/client';
import {
  EventEmitterWithIndex,
  getCurrentUserHook,
  getTranslatedText,
  COMMON_CONSTANTS,
  EventEmitter
} from '@nile/components';
import Tippy from '@tippyjs/react';
import {
  useCachedQuery,
  useNoCacheQuery,
  GET_ALL_TIMER_ENTRIES,
  TicketClient,
  GET_GLOBAL_TIMER_ENTRIES
} from '@nile/graphql';
import { isEqual, min } from 'lodash';
import { Tooltip } from 'react-clone-tippy';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import RunningTimers from './RunningTimers';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

const SectionHolder = styled.div`
  display: flex;
  color: #fff;
  align-items: center;
  flex-direction: column;
  font-size: 10px;
  text-transform: uppercase;
  width: 100%;
  position: relative;
`;

const CountBulb = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  background: #ff0055;
  position: absolute;
  top: -10px;
  right: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
`;

function GlobalTimerSection(props) {
  const intl = useIntl();

  // const [mounted, setMounted] = useState(true);
  // const [timerEntries, setTimerEntries] = useState(mockedData);

  const globalTimerTippyRef = useRef();
  const isItProduct = useCheckIsItProduct();
  const user = getCurrentUserHook();
  const [mounted, setMounted] = useState(false);
  const [listHeight, setListHeight] = useState(275);
  let timer = null;
  const popupParentName = 'RunningTimersPopup';
  const {
    loading,
    data: runningTimerEntries,
    error,
    refetch
  } = useNoCacheQuery(GET_GLOBAL_TIMER_ENTRIES, {
    client: TicketClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      isItProduct,
      worklogTimerInput: user?.userId
        ? {
            listInfo: {
              condition: {
                joinOperator: 'AND',
                operands: {
                  attribute: 'technician.userId',
                  operator: 'is',
                  value: user?.userId
                }
              },
              sort: [
                {
                  attribute: 'stateChangeTime',
                  order: 'DESC'
                }
              ]
            }
          }
        : {}
    },
    onCompleted(response) {}
  });

  const refetchEntries = () => {
    refetch();
  };

  function setFormHeight() {
    const documentHeight = document.body.getBoundingClientRect().height;
    const popupHeight = document
      .getElementById('globaltimerPopup')
      ?.getBoundingClientRect().height;
    const formHeight = document
      .getElementById('FormContainer')
      ?.getBoundingClientRect().height;
    const overallHeight = min([documentHeight, popupHeight]);
    if (overallHeight && formHeight) {
      const newHeight = overallHeight - formHeight;
      if (!isEqual(newHeight, listHeight)) setListHeight(newHeight);
    }
  }

  function repositionPopup() {
    if (globalTimerTippyRef.current._tippy) {
      globalTimerTippyRef.current._tippy.popperInstance.forceUpdate();
      setFormHeight();
    }
  }

  useEffect(() => {
    const eventIndex = EventEmitterWithIndex.subscribe(
      'refetchGlobalTimers',
      refetchEntries
    );
    return function cleanup() {
      EventEmitterWithIndex.unsubscribe('refetchGlobalTimers', eventIndex);
    };
  }, []);

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName).length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  function closePopover() {
    setMounted(false);
    globalTimerTippyRef.current._tippy.hide();
    setListHeight(275);
    // document.removeEventListener('click', handleOutsideClick);
  }

  function handleOutsideClick(e) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const clickedOutside = [];
      [
        'EditTimerParent',
        'AssociateWorkItemParent',
        'RunningTimersPopup'
      ].forEach((pName) => {
        document.getElementsByClassName(pName).forEach((popup) => {
          if (
            popup?._tippy?.state?.isVisible &&
            popup._tippy.popper.contains(e.target)
          ) {
            clickedOutside.push(true);
          }
        });
      });
      if (!clickedOutside.some((res) => res)) {
        closePopover();
      }
    }, 20);
  }

  const togglePopover = () => {
    if (globalTimerTippyRef?.current?._tippy) {
      if (!globalTimerTippyRef.current?._tippy?.state?.isVisible) {
        setMounted(true);
        closeAllOpenPopups();
        globalTimerTippyRef.current._tippy.show();
        window?.CommandBar?.trackEvent('openGlobalTimer');
        // document.addEventListener('click', handleOutsideClick);
      } else if (globalTimerTippyRef.current?._tippy?.state?.isVisible) {
        // EventEmitter.dispatch('resetWorklogContent');
        closePopover();
      }
    }
  };

  const closeGlobalTimer = () => {
    if (globalTimerTippyRef?.current?._tippy) {
      if (globalTimerTippyRef.current?._tippy?.state?.isVisible) {
        // EventEmitter.dispatch('resetWorklogContent');
        closePopover();
        closeAllOpenPopups();
      }
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('closeGlobalTimer', closeGlobalTimer);
    return () => {
      EventEmitter.unsubscribe('closeGlobalTimer');
    };
  }, []);

  return (
    <Tippy
      content={
        mounted ? (
          <div onChange={repositionPopup}>
            <RunningTimers
              loading={loading}
              listHeight={listHeight}
              setFormHeight={setFormHeight}
              refetch={refetchEntries}
              tabContext={props.tabRef}
              runningTimerEntries={
                runningTimerEntries?.fetchWorklogTimerEntries?.entries || []
              }
              associateWorkItem
              handleClose={togglePopover}
              repositionPopup={repositionPopup}
            />
          </div>
        ) : null
      }
      placement="right"
      arrow={false}
      interactive
      trigger="manual"
      theme="light"
      allowHTML
      appendTo={document.body}
      ref={(node) => {
        globalTimerTippyRef.current = node;
      }}
      onShow={() => {
        // if (!globalTimerTippyRef.current._tippy.state?.isVisible) {
        //   globalTimerTippyRef.current._tippy.show();
        // }
        refetchEntries();
      }}
      onHide={() => {
        // if (globalTimerTippyRef.current._tippy.state?.isVisible) {
        //   globalTimerTippyRef.current._tippy.hide();
        // }
      }}
      offset={[-100, 20]}
      maxWidth={540}
      hideOnClick="toggle"
      zIndex={500}
    >
      <div
        id="app-worklog-timer"
        className={`appIcon flex center ${popupParentName}`}
      >
        <Tooltip
          title={getTranslatedText(intl, 'app.navigation.timers')}
          position="right"
          inertia
          delay={200}
          trigger="mouseenter"
          animation="shift"
          animateFill={false}
          className="full-width"
        >
          <SectionHolder onClick={togglePopover}>
            <div>
              <SVGIcon name="Worklog" color="var(--sops-app-nav-text-color)" />
            </div>
            {runningTimerEntries?.fetchWorklogTimerEntries?.entries?.length >
            0 ? (
              <CountBulb>
                {runningTimerEntries?.fetchWorklogTimerEntries?.entries?.length}
              </CountBulb>
            ) : (
              ''
            )}
          </SectionHolder>
        </Tooltip>
      </div>
    </Tippy>
  );
}

export default GlobalTimerSection;
